import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  Inject,
  Optional,
  OnInit,
} from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { ApiService } from '../../../services/api.service';

/**
 * Generic grid component
 * Usage:
 * <app-grid [rows]="items" (edit)="onEdit($event)" (delete)="onDelete($event)"></app-grid>
 *
 * The component infers columns from the first row's own enumerable properties
 * unless you pass an explicit `columns` input.
 */
@Component({
  selector: 'app-grid',
  imports: [],
  templateUrl: './grid.component.html',
  styleUrl: './grid.component.scss',
})
export class GridComponent implements OnChanges, OnInit {
  // rows of generic objects to render
  @Input() rows: any[] | null = null;

  // alternatively: fetch rows from this URL (relative paths use ApiService)
  @Input() dataUrl: string | null = null;

  // optional override for columns (array of property names)
  @Input() columns: string[] | null = null;

  // show action buttons column
  @Input() showActions = true;

  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() create = new EventEmitter<void>();

  // derived columns computed from first row or from `columns` input
  public derivedColumns: string[] = [];

  loading = false;
  error: string | null = null;

  constructor(private http: HttpClient, @Optional() private api?: ApiService) {}

  ngOnInit(): void {
    if (this.dataUrl && (!this.rows || !this.rows.length)) {
      this.fetchDataUrl();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns'] && this.columns && this.columns.length) {
      this.derivedColumns = [...this.columns];
      return;
    }

    if (changes['dataUrl'] && this.dataUrl) {
      this.fetchDataUrl();
      return;
    }

    if (changes['rows']) {
      this.computeColumnsFromRows();
    }
  }

  private fetchDataUrl() {
    if (!this.dataUrl) return;
    this.loading = true;
    this.error = null;

    // prefer ApiService for relative app paths (starts with '/') so runtime baseUrl is applied
    const isRelative = this.dataUrl.startsWith('/');

    const obs =
      isRelative && this.api
        ? this.api.get<any[]>(this.dataUrl)
        : this.http.get<any[]>(this.dataUrl);

    obs.subscribe({
      next: (data: any[]) => {
        this.rows = data ?? [];
        this.computeColumnsFromRows();
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Grid fetch failed', err);
        this.error = err?.message ?? 'Failed to fetch data';
        this.loading = false;
      },
    });
  }

  private computeColumnsFromRows() {
    if (this.columns && this.columns.length) {
      this.derivedColumns = [...this.columns];
      return;
    }

    const first = this.rows && this.rows.length ? this.rows[0] : null;
    if (!first) {
      this.derivedColumns = [];
      return;
    }

    // Only own enumerable string keys
    this.derivedColumns = Object.keys(first).filter(
      (k) => typeof k === 'string'
    );
  }

  onEdit(row: any) {
    this.edit.emit(row);
  }

  onDelete(row: any) {
    this.delete.emit(row);
  }
  onCreate() {
    this.create.emit();
  }

  // helper to render a cell value simply
  displayValue(value: any): string {
    if (value == null) return '';
    if (value instanceof Date) return value.toLocaleDateString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
}
